import { z } from "zod";

// ---------------------------------------------------------------------------
// BessConfigSnapshot (Phase 7.4) — Cross-Repo-Vertrag Web-Konfigurator → OS
// ---------------------------------------------------------------------------
// Der oeffentliche BESS-Konfigurator (`berglerenergie-web`, Baustein 7.3)
// schreibt beim „Angebot anfordern" einen Konfig-Snapshot in
// `metadata.bess_config` des Lead-Intake-Payloads (`lead_type =
// "bess_configurator"`). Dieses Schema validiert genau diesen Snapshot auf der
// OS-Seite.
//
// Der Vertrag stammt aus `src/lib/bess-uvp-config.ts` (`BessConfigSnapshot`)
// im Web-Repo:
//   { source:"public_configurator", version:1, currency:"CHF",
//     lines:[{ sku, productCode, name, qty, unitUvpChf, lineUvpChf, kwh, kw,
//              options:{ ecc, ups, eccpMulti, rahmen, wanne, transport,
//                        inobat } }],
//     totalUvpChf, generatedAtIso }
//
// Design (Daniel-Spec 7.4): strikt auf den Pflicht-Feldern (lines, qty,
// totalUvp), aber **tolerant gegen unbekannte addOn-Keys** (forward-compat).
// Da der genaue Werteformat der `options`-Felder im anderen Repo lebt und sich
// erweitern kann, sind die Option-Werte `unknown` + `.passthrough()`. Das
// Mapping auf einen konkreten `BessSeedInput` passiert defensiv im
// `leadToProjectService` (OS-Seite), nicht hier — `shared/` haengt nicht von
// der Seed-Engine ab.

// Eine einzelne Cart-Position. Pflicht: sku + productCode + qty. Preis-/
// Kennzahl-Felder sind optional-nullable (Zubehoer hat keine Public-UVP, nur
// Batterien tragen `unitUvpChf`/`lineUvpChf` — siehe 7.3-Design).
export const bessConfigSnapshotLineSchema = z
  .object({
    sku: z.string().min(1),
    productCode: z.string().min(1),
    name: z.string().nullable().optional(),
    // qty=0 ist ungueltig (eine Position mit Menge 0 ist keine Position).
    qty: z.number().int().positive(),
    unitUvpChf: z.number().nonnegative().nullable().optional(),
    lineUvpChf: z.number().nonnegative().nullable().optional(),
    kwh: z.number().nonnegative().nullable().optional(),
    kw: z.number().nonnegative().nullable().optional(),
    // Tolerant: bekannte addOn-Keys sind dokumentiert, Werte aber `unknown`
    // (Cross-Repo-Format), und unbekannte Keys ueberleben via passthrough.
    options: z
      .object({
        ecc: z.unknown().optional(),
        ups: z.unknown().optional(),
        eccpMulti: z.unknown().optional(),
        rahmen: z.unknown().optional(),
        wanne: z.unknown().optional(),
        transport: z.unknown().optional(),
        inobat: z.unknown().optional(),
      })
      .passthrough()
      .optional()
      .default({}),
  })
  .passthrough();
export type BessConfigSnapshotLine = z.infer<typeof bessConfigSnapshotLineSchema>;

export const bessConfigSnapshotSchema = z
  .object({
    source: z.string().optional(),
    version: z.number().optional(),
    currency: z.string().optional(),
    lines: z.array(bessConfigSnapshotLineSchema).min(1),
    // totalUvpChf ist der Anker fuer den Drift-Check. Tolerant optional — wenn
    // er fehlt, rekonstruiert `bessSnapshotTotalUvp` ihn aus den Zeilen.
    totalUvpChf: z.number().nonnegative().optional(),
    generatedAtIso: z.string().optional(),
  })
  .passthrough();
export type BessConfigSnapshot = z.infer<typeof bessConfigSnapshotSchema>;

export const BESS_CONFIGURATOR_LEAD_TYPE = "bess_configurator" as const;

/**
 * Discriminator: ist dieser Lead aus dem oeffentlichen BESS-Konfigurator?
 * Liest `metadata.lead_type === "bess_configurator"` defensiv.
 */
export function isBessConfiguratorLead(metadata: unknown): boolean {
  if (metadata === null || typeof metadata !== "object") return false;
  const leadType = (metadata as Record<string, unknown>).lead_type;
  return leadType === BESS_CONFIGURATOR_LEAD_TYPE;
}

/**
 * Liest + validiert den Snapshot aus `metadata.bess_config`. Liefert `null`,
 * wenn kein/ungueltiger Snapshot vorliegt (Caller behandelt das als „kein
 * BESS-Lead").
 */
export function readBessConfigSnapshot(
  metadata: unknown,
): BessConfigSnapshot | null {
  if (metadata === null || typeof metadata !== "object") return null;
  const raw = (metadata as Record<string, unknown>).bess_config;
  if (raw === null || raw === undefined) return null;
  const parsed = bessConfigSnapshotSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Effektive Snapshot-UVP. Bevorzugt das vom Web gelieferte `totalUvpChf`,
 * faellt sonst auf die Summe der Zeilen zurueck (`lineUvpChf`, ersatzweise
 * `unitUvpChf × qty`).
 */
export function bessSnapshotTotalUvp(snapshot: BessConfigSnapshot): number {
  if (typeof snapshot.totalUvpChf === "number") return snapshot.totalUvpChf;
  return snapshot.lines.reduce((sum, line) => {
    if (typeof line.lineUvpChf === "number") return sum + line.lineUvpChf;
    if (typeof line.unitUvpChf === "number") {
      return sum + line.unitUvpChf * line.qty;
    }
    return sum;
  }, 0);
}

export interface BessSnapshotSummary {
  readonly lineCount: number;
  readonly totalQty: number;
  readonly totalUvpChf: number;
  readonly skus: readonly string[];
}

/**
 * Kompakte Zusammenfassung fuer Inbox-Card + Notification: Anzahl Positionen,
 * Gesamt-Menge, UVP-Total und die SKU-Liste (z.B. fuer Chip-Anzeige).
 */
export function summarizeBessSnapshot(
  snapshot: BessConfigSnapshot,
): BessSnapshotSummary {
  return {
    lineCount: snapshot.lines.length,
    totalQty: snapshot.lines.reduce((sum, line) => sum + line.qty, 0),
    totalUvpChf: bessSnapshotTotalUvp(snapshot),
    skus: snapshot.lines.map((line) => line.sku),
  };
}
